import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import "./LanguageSwitcher.css";

// Bandeiras em SVG (emoji de bandeira não aparece no Windows). Quadradas e recortadas em círculo pelo CSS.
const FLAGS: Record<string, React.ReactNode> = {
  pt: (
    <>
      <rect width="24" height="24" fill="#009C3B" />
      <path d="M12 3.5 22 12 12 20.5 2 12z" fill="#FFDF00" />
      <circle cx="12" cy="12" r="4.6" fill="#002776" />
      <path d="M7.6 10.9c2.9-.5 6.1.2 8.6 1.9" stroke="#fff" strokeWidth=".9" fill="none" />
    </>
  ),
  en: (
    <>
      <rect width="24" height="24" fill="#fff" />
      {[0, 2, 4, 6, 8, 10, 12].map((i) => (
        <rect key={i} y={(i * 24) / 13} width="24" height={24 / 13} fill="#B22234" />
      ))}
      <rect width="12" height={(7 * 24) / 13} fill="#3C3B6E" />
      {[[3, 3], [6, 3], [9, 3], [4.5, 6.5], [7.5, 6.5], [3, 10], [6, 10], [9, 10]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r=".7" fill="#fff" />
      ))}
    </>
  ),
  es: (
    <>
      <rect width="24" height="24" fill="#AA151B" />
      <rect y="6" width="24" height="12" fill="#F1BF00" />
    </>
  ),
};

const LANGUAGES = [
  { code: 'pt', title: 'Português' },
  { code: 'en', title: 'English' },
  { code: 'es', title: 'Español' },
];

const Flag = ({ code }: { code: string }) => (
  <svg className="cz-lang__flag" viewBox="0 0 24 24" aria-hidden="true">
    {FLAGS[code]}
  </svg>
);

// dropdown: só a bandeira do idioma atual, abrindo a lista (header). Sem ele, as três bandeiras lado a lado (menu mobile).
const LanguageSwitcher: React.FC<{ dropdown?: boolean }> = ({ dropdown }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGUAGES.find(({ code }) => i18n.language.startsWith(code)) ?? LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const select = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  if (dropdown) {
    return (
      <div ref={ref} className={`cz-lang-menu${open ? ' is-open' : ''}`}>
        <button
          type="button"
          className="cz-lang-menu__trigger"
          aria-label={`${t('header.language', 'Idioma')}: ${current.title}`}
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpen((v) => !v)}
        >
          <Flag code={current.code} />
          <ChevronDown size={14} strokeWidth={2.2} className="cz-lang-menu__chevron" aria-hidden="true" />
        </button>
        {open && (
          <ul className="cz-lang-menu__list">
            {LANGUAGES.map(({ code, title }) => (
              <li key={code}>
                <button
                  type="button"
                  className={`cz-lang-menu__option${code === current.code ? ' is-active' : ''}`}
                  aria-current={code === current.code}
                  onClick={() => select(code)}
                >
                  <Flag code={code} />
                  {title}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="cz-lang" role="group" aria-label={t('header.language', 'Idioma')}>
      {LANGUAGES.map(({ code, title }) => {
        const active = code === current.code;
        return (
          <button
            key={code}
            type="button"
            className={`cz-lang__btn${active ? ' is-active' : ''}`}
            onClick={() => select(code)}
            title={title}
            aria-label={title}
            aria-pressed={active}
          >
            <Flag code={code} />
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
